/**
 * User Management Component
 * Allows admins to assign roles and block users
 */

import React, { useState, useEffect } from 'react';
import useStore from '../store/useStore';
import { format } from 'date-fns';
import './UserManagement.css';

const UserManagement = () => {
  const { user, userRole } = useStore();
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showAddUser, setShowAddUser] = useState(false);
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserRole, setNewUserRole] = useState('Viewer');
  const [editingUser, setEditingUser] = useState(null);

  const availableRoles = ['Viewer', 'AssetManager', 'Executive', 'Admin', 'Blocked'];

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    setIsLoading(true);
    try {
      const sharePointService = (await import('../services/sharePointService')).default;
      const roleAssignments = await sharePointService.getRoleAssignments();
      setUsers(roleAssignments);
    } catch (error) {
      console.error('Error loading users:', error);
      alert('Failed to load users');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddUser = async () => {
    if (!newUserEmail.trim()) {
      alert('Please enter an email address');
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newUserEmail)) {
      alert('Please enter a valid email address');
      return;
    }

    // Check if user already exists
    if (users.some(u => u.UserEmail.toLowerCase() === newUserEmail.toLowerCase())) {
      alert('User already exists');
      return;
    }

    try {
      const sharePointService = (await import('../services/sharePointService')).default;
      await sharePointService.createRoleAssignment({
        Title: newUserEmail,
        UserEmail: newUserEmail,
        Role: newUserRole,
        AssignedBy: user.username || user.email,
        AssignedOn: new Date().toISOString(),
        Notes: `Added by ${user.name || user.username}`
      });

      setNewUserEmail('');
      setNewUserRole('Viewer');
      setShowAddUser(false);
      await loadUsers();
      alert('User added successfully');
    } catch (error) {
      console.error('Error adding user:', error);
      alert('Failed to add user');
    }
  };

  const handleUpdateRole = async (userId, newRole) => {
    try {
      const sharePointService = (await import('../services/sharePointService')).default;
      await sharePointService.updateRoleAssignment(userId, {
        Role: newRole,
        AssignedBy: user.username || user.email,
        AssignedOn: new Date().toISOString()
      });

      await loadUsers();
      setEditingUser(null);
      alert('Role updated successfully');
    } catch (error) {
      console.error('Error updating role:', error);
      alert('Failed to update role');
    }
  };

  const handleDeleteUser = async (userId, userEmail) => {
    if (!window.confirm(`Remove ${userEmail} from the system?`)) {
      return;
    }

    try {
      const sharePointService = (await import('../services/sharePointService')).default;
      await sharePointService.deleteRoleAssignment(userId);
      await loadUsers();
      alert('User removed successfully');
    } catch (error) {
      console.error('Error deleting user:', error);
      alert('Failed to remove user');
    }
  };

  const getRoleBadgeClass = (role) => {
    const classes = {
      'Admin': 'role-admin',
      'AssetManager': 'role-asset-manager',
      'Executive': 'role-executive',
      'Viewer': 'role-viewer',
      'Blocked': 'role-blocked'
    };
    return classes[role] || 'role-viewer';
  };

  const getRoleIcon = (role) => {
    const icons = {
      'Admin': '👑',
      'AssetManager': '⚙️',
      'Executive': '📊',
      'Viewer': '👁️',
      'Blocked': '🚫'
    };
    return icons[role] || '👤';
  };

  if (userRole !== 'Admin') {
    return (
      <div className="user-management">
        <div className="access-denied">
          <h2>🔒 Access Denied</h2>
          <p>Only administrators can access user management.</p>
          <p>Your current role: <strong>{userRole}</strong></p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="user-management">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading users...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="user-management">
      <div className="user-management-header">
        <div>
          <h2>👥 User Management</h2>
          <p className="subtitle">Manage user access and roles</p>
        </div>
        <button 
          className="btn-add-user"
          onClick={() => setShowAddUser(!showAddUser)}
        >
          {showAddUser ? '✕ Cancel' : '+ Add User'}
        </button>
      </div>

      {/* Add User Form */}
      {showAddUser && (
        <div className="add-user-form">
          <h3>Add New User</h3>
          <div className="form-row">
            <div className="form-group">
              <label>Email Address</label>
              <input
                type="email"
                value={newUserEmail}
                onChange={(e) => setNewUserEmail(e.target.value)}
                placeholder="user@company.com"
              />
            </div>
            <div className="form-group">
              <label>Role</label>
              <select
                value={newUserRole}
                onChange={(e) => setNewUserRole(e.target.value)}
              >
                {availableRoles.filter(r => r !== 'Blocked').map(role => (
                  <option key={role} value={role}>{role}</option>
                ))}
              </select>
            </div>
            <button className="btn-submit" onClick={handleAddUser}>
              Add User
            </button>
          </div>
        </div>
      )}

      {/* Role Summary */}
      <div className="role-summary">
        <div className="summary-card">
          <div className="summary-icon">👥</div>
          <div className="summary-content">
            <div className="summary-label">Total Users</div>
            <div className="summary-value">{users.length}</div>
          </div>
        </div>

        {availableRoles.map(role => {
          const count = users.filter(u => u.Role === role).length;
          return (
            <div key={role} className="summary-card">
              <div className="summary-icon">{getRoleIcon(role)}</div>
              <div className="summary-content">
                <div className="summary-label">{role}</div>
                <div className="summary-value">{count}</div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Users Table */}
      <div className="users-table-container">
        <table className="users-table">
          <thead>
            <tr>
              <th>Email</th>
              <th>Current Role</th>
              <th>Assigned By</th>
              <th>Assigned On</th>
              <th>Notes</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.length === 0 ? (
              <tr>
                <td colSpan="6" className="empty-state">
                  No users found. Click "Add User" to add the first user.
                </td>
              </tr>
            ) : (
              users.map(userItem => (
                <tr key={userItem.Id} className={userItem.Role === 'Blocked' ? 'blocked-user' : ''}>
                  <td className="user-email">
                    <div className="email-cell">
                      <span className="email">{userItem.UserEmail}</span>
                      {userItem.UserEmail.toLowerCase() === user.username?.toLowerCase() && (
                        <span className="badge-current-user">You</span>
                      )}
                    </div>
                  </td>
                  
                  <td>
                    {editingUser === userItem.Id ? (
                      <select
                        value={userItem.Role}
                        onChange={(e) => handleUpdateRole(userItem.Id, e.target.value)}
                        className="role-select"
                        autoFocus
                      >
                        {availableRoles.map(role => (
                          <option key={role} value={role}>{role}</option>
                        ))}
                      </select>
                    ) : (
                      <span className={`role-badge ${getRoleBadgeClass(userItem.Role)}`}>
                        {getRoleIcon(userItem.Role)} {userItem.Role}
                      </span>
                    )}
                  </td>
                  
                  <td className="assigned-by">{userItem.AssignedBy || '-'}</td>
                  
                  <td className="assigned-on">
                    {userItem.AssignedOn ? 
                      format(new Date(userItem.AssignedOn), 'MMM dd, yyyy') : 
                      '-'}
                  </td>
                  
                  <td className="notes">{userItem.Notes || '-'}</td>
                  
                  <td className="actions">
                    {editingUser === userItem.Id ? (
                      <button
                        className="btn-cancel-edit"
                        onClick={() => setEditingUser(null)}
                      >
                        ✕ Cancel
                      </button>
                    ) : (
                      <div className="action-buttons">
                        <button
                          className="btn-edit-role"
                          onClick={() => setEditingUser(userItem.Id)}
                          disabled={userItem.UserEmail.toLowerCase() === user.username?.toLowerCase()}
                          title={userItem.UserEmail.toLowerCase() === user.username?.toLowerCase() ? 
                            "You cannot change your own role" : "Change role"}
                        >
                          ✎ Edit Role
                        </button>
                        <button
                          className="btn-delete-user"
                          onClick={() => handleDeleteUser(userItem.Id, userItem.UserEmail)}
                          disabled={userItem.UserEmail.toLowerCase() === user.username?.toLowerCase()}
                          title={userItem.UserEmail.toLowerCase() === user.username?.toLowerCase() ? 
                            "You cannot remove yourself" : "Remove user"}
                        >
                          🗑 Remove
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Role Descriptions */}
      <div className="role-descriptions">
        <h3>Role Permissions</h3>
        <div className="role-cards">
          <div className="role-card">
            <div className="role-card-header">
              <span className="role-card-icon">👁️</span>
              <h4>Viewer</h4>
            </div>
            <ul>
              <li>View dashboard and metrics</li>
              <li>View entities and signals</li>
              <li>View alerts and reports</li>
              <li>No edit permissions</li>
            </ul>
          </div>

          <div className="role-card">
            <div className="role-card-header">
              <span className="role-card-icon">⚙️</span>
              <h4>AssetManager</h4>
            </div>
            <ul>
              <li>All Viewer permissions</li>
              <li>Add/edit entities</li>
              <li>Add/edit signals</li>
              <li>Add/edit rules</li>
              <li>Upload Excel data</li>
              <li>Resolve alerts</li>
            </ul>
          </div>

          <div className="role-card">
            <div className="role-card-header">
              <span className="role-card-icon">📊</span>
              <h4>Executive</h4>
            </div>
            <ul>
              <li>All Viewer permissions</li>
              <li>Access to executive reports</li>
              <li>Export capabilities</li>
              <li>No data modification</li>
            </ul>
          </div>

          <div className="role-card">
            <div className="role-card-header">
              <span className="role-card-icon">👑</span>
              <h4>Admin</h4>
            </div>
            <ul>
              <li>Full system access</li>
              <li>User management</li>
              <li>Role assignments</li>
              <li>System configuration</li>
            </ul>
          </div>

          <div className="role-card blocked">
            <div className="role-card-header">
              <span className="role-card-icon">🚫</span>
              <h4>Blocked</h4>
            </div>
            <ul>
              <li>No access to system</li>
              <li>Cannot login</li>
              <li>Used to revoke access</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserManagement;